import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';

/**
 * Mapa central v1 → v2 — cierre del rediseño del portal del agente.
 *
 * El rediseño YA es el portal (steering `02-portal-agente-base.md`): esta tabla
 * hace que ninguna ruta v1 sea alcanzable (URL tecleada, enlace interno, login,
 * impersonate o catch-all) sin borrar ni una vista v1. Cada ruta v1 que tenía
 * `component` pierde el componente y pasa a un `beforeEnter` que llama a una de
 * estas funciones, que decide el destino v2 y hace `next({ name, params?, query? })`.
 *
 * Mapa aprobado por el usuario (ver tabla completa en el reporte de la task):
 *   dashboard/main · activity/list · activity/detail/:activity_id ·
 *   activity/detail/v2/:hash_folio · policy/list · policy/edit/* ·
 *   policy/edit-renovation/* · client/list · client/edit/:client_hash ·
 *   receipt/list · renovation/list · user/profile · insurance/list ·
 *   insurance/config/:id · support/list · support/config/:hash · quote/list ·
 *   multiquote/* · emission/edit/:policy_hash · /folios · /folios/:hash ·
 *   /clientes · catch-all.
 *
 * `/folios`, `/folios/:hash`, `/clientes`, `/clientes/:hash` ya SON v2 (alias
 * locales declarados en `activityLogs.ts`/`clients.ts`) y no se tocan aquí.
 *
 * `next` usa el tipo oficial `NavigationGuardNext` de vue-router: `beforeEnter`
 * exige una `NavigationGuard`, y una firma más laxa (`(to?: any) => void`) no es
 * asignable a ese tipo (TS2322). `MainRoutes` (`PrivateRoutes.ts`) está anotado
 * como `RouteRecordRaw` a propósito para que cada `beforeEnter`/`component` se
 * valide contra la forma oficial en vez de depender de inferencia de literales.
 */

type Redirect = (this: void, to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => void;

/** Simple: la ruta v1 siempre cae al mismo destino v2, sin traducir params. */
function toFixed(name: string): Redirect {
    return (_to, _from, next) => next({ name, replace: true });
}

/**
 * Mis Folios v2: lista. Usada por toda ruta v1 que no tiene traducción de
 * detalle disponible en el guard (activity/list, quote/list, multiquote/*,
 * emission/edit/:policy_hash, y activity/detail/:activity_id —ver abajo—).
 */
export const redirectToFoliosList: Redirect = toFixed('Mis Folios v2');

/** Mi Tablero v2: catch-all y destinos sin equivalente directo. */
export const redirectToTablero: Redirect = toFixed('Mi Tablero v2');

/** Mis Pólizas v2: lista (receipt/list, renovation/list, policy/list). */
export const redirectToPoliciesList: Redirect = toFixed('Mis Pólizas v2');

/** Mis Clientes v2: lista. */
export const redirectToClientsList: Redirect = toFixed('Mis Clientes v2');

/** Mi Perfil v2 (sin pestaña concreta). */
export const redirectToProfile: Redirect = toFixed('Mi Perfil v2');

/** Soporte v2: lista. */
export const redirectToSupportList: Redirect = toFixed('Soporte v2');

/**
 * `activity/detail/:activity_id` — v1 solo trae un ID numérico, no el hash que
 * pide `Mis Folios v2 Detalle`. No hay forma de traducir id→hash sin una
 * llamada a API dentro del guard (fuera de alcance de un redirect de routing);
 * por mapa aprobado, cae a la LISTA de Mis Folios v2 en vez de bloquear la
 * navegación.
 */
export const redirectActivityDetailById: Redirect = redirectToFoliosList;

/**
 * `activity/detail/v2/:hash_folio` — este sí trae hash: va directo al detalle
 * v2 con el mismo hash, conservando query útil (ninguna hoy, pero no se pierde
 * si en el futuro se agrega).
 */
export const redirectActivityDetailByHash: Redirect = (to, from, next) => {
    const hash = String(to.params.hash_folio ?? '').trim();
    if (!hash) return redirectToFoliosList(to, from, next);
    next({ name: 'Mis Folios v2 Detalle', params: { hash }, query: to.query, replace: true });
};

/**
 * `policy/edit/:hash` y `policy/edit-renovation/:hash` — Mis Pólizas v2 abre el
 * detalle de una póliza vía `query.hash` (mismo patrón que usa
 * `useActivityLogs.ts` al navegar a `{ name: 'Mis Pólizas v2', query: { hash } }`).
 * Sin hash útil, cae a la lista.
 */
export const redirectPolicyEditByHash: Redirect = (to, from, next) => {
    const hash = String(to.params.hash ?? '').trim();
    if (!hash) return redirectToPoliciesList(to, from, next);
    next({ name: 'Mis Pólizas v2', query: { ...to.query, hash }, replace: true });
};

/**
 * `client/edit/:client_hash` — Mis Clientes v2 Detalle usa `:hash` como param
 * (no query). Sin hash útil, cae a la lista.
 */
export const redirectClientEditByHash: Redirect = (to, from, next) => {
    const hash = String(to.params.client_hash ?? '').trim();
    if (!hash) return redirectToClientsList(to, from, next);
    next({ name: 'Mis Clientes v2 Detalle', params: { hash }, query: to.query, replace: true });
};

/**
 * `insurance/list` y `insurance/config/:insurance_id` — Mis Claves de Agente
 * (v1) pasa a ser la pestaña "Claves" de Mi Perfil v2, seleccionada por hash de
 * URL (`#claves`, mismo mecanismo que `useProfile.ts`). No hay traducción de
 * `insurance_id` a un filtro de esa pestaña hoy: entra a la pestaña completa.
 */
export const redirectToProfileClaves: Redirect = (_to, _from, next) => next({ name: 'Mi Perfil v2', hash: '#claves', replace: true });
